"use client";

import { useEffect, useMemo, useState } from "react";

import { mergePreviewDataWithTranslations } from "@/components/QRPreview/utils";
import { INITIAL_QR_DATA } from "@/contexts/QREditorContext";
import type { PreviewAdapterProps } from "../components/PreviewAdapter";

import { previewRegistry } from "../registry";
import { InitPreviewData, UseEditorPreviewRenderArgsParams } from "./types";
import { logError } from "@/services/DatadogService";
import {
  extractPreviewImageUrls,
  preloadPreviewImages,
} from "@/features/previews/shared/utils/previewImage";

/**
 * Editor-only: merges init preview JSON with translations, resolves theme color for init state,
 * and returns props for {@link PreviewAdapter}. Returns null when `data.type` is missing.
 */
export const useEditorPreviewRenderArgs = ({
  data,
  previewTranslations,
  isQRInit,
}: UseEditorPreviewRenderArgsParams): PreviewAdapterProps | null => {
  const [initData, setInitData] = useState<InitPreviewData>(null);
  const isActualPreview = data?.currentPageIndex > 0 && !isQRInit;

  useEffect(() => {
    let isMounted = true;

    const fetchInitData = async () => {
      if (!data?.type || isActualPreview) return;
      try {
        const importedData = await previewRegistry.getInitialData(data.type);

        if (isMounted && importedData) {
          preloadPreviewImages(extractPreviewImageUrls(importedData));
          setInitData(importedData);
        }
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          component: "useEditorPreviewRenderArgs",
          action: "fetchInitPreviewData",
          qrType: data.type,
        });
      }
    };
    fetchInitData();

    return () => {
      isMounted = false;
    };
  }, [data?.type, isActualPreview]);

  const finalInitPreviewData = useMemo(() => {
    if (!data?.type || !initData) {
      return undefined;
    }
    return mergePreviewDataWithTranslations(initData, previewTranslations, data.type);
  }, [data?.type, previewTranslations, initData]);

  if (!data?.type) {
    return null;
  }

  const getThemeColor = () => {
    const { themeColor } = data?.data || {};
    const initialTheme = INITIAL_QR_DATA.data.themeColor;

    const hasChanged =
      themeColor?.isCustom ||
      (themeColor &&
        (themeColor.backgroundColor !== initialTheme.backgroundColor ||
          themeColor.buttonColor !== initialTheme.buttonColor));

    return data?.currentPageIndex > 0 && hasChanged
      ? themeColor
      : finalInitPreviewData?.data.themeColor;
  };

  if (isActualPreview) {
    return {
      ...data,
      previewTranslations,
      preview: true,
      source: "editor",
    };
  }

  if (!finalInitPreviewData) {
    return null;
  }

  const componentProps = {
    ...finalInitPreviewData,
    data: {
      ...finalInitPreviewData.data,
      themeColor: getThemeColor(),
      ...(data?.data?.logoUrl && { logoUrl: data.data.logoUrl }),
    },
    initPreview: true,
  };

  return {
    ...componentProps,
    previewTranslations,
    preview: true,
    source: "editor",
  };
};
